import SortableJs from 'sortablejs'

export type SortableElement = HTMLElement

export type SortableEvent = {
  oldIndex?: number
  newIndex?: number
  from?: HTMLElement
  to?: HTMLElement
  item?: HTMLElement
  clone?: HTMLElement
  pullMode?: true | false | 'clone'
}

export type SortableOptions = {
  animation?: number
  disabled?: boolean
  draggable?: string
  handle?: string
  filter?: string | ((event: Event, target: HTMLElement, sortable: unknown) => boolean)
  preventOnFilter?: boolean
  ghostClass?: string
  chosenClass?: string
  dragClass?: string
  group?: string | Record<string, unknown>
  direction?: 'vertical' | 'horizontal' | ((event: Event, target: HTMLElement, dragElement: HTMLElement) => 'vertical' | 'horizontal')
  forceFallback?: boolean
  fallbackClass?: string
  fallbackOnBody?: boolean
  fallbackTolerance?: number
  swapThreshold?: number
  invertSwap?: boolean
  invertedSwapThreshold?: number
  onStart?: (event: SortableEvent) => void
  onEnd?: (event: SortableEvent) => void
  onAdd?: (event: SortableEvent) => void
  onUpdate?: (event: SortableEvent) => void
  onSort?: (event: SortableEvent) => void
  onRemove?: (event: SortableEvent) => void
  onMove?: (event: SortableEvent, originalEvent: Event) => boolean | -1 | 1 | void
  onChoose?: (event: SortableEvent) => void
  onUnchoose?: (event: SortableEvent) => void
  [key: string]: unknown
}

export type SortableInstance = {
  destroy(): void
  option(name: string): unknown
  option(name: string, value: unknown): void
  sort(order: string[], useAnimation?: boolean): void
  toArray(): string[]
}

export type ManagedSortableInstance = SortableInstance & {
  release(): void
}

export type SortableOwner = {
  context?: {
    addUnnamedResource?: (type: string, value: unknown) => void
  }
  addUnnamedResource?: (type: string, value: unknown) => void
}

export type ReorderResult<T> = {
  items: T[]
  item: T
  oldIndex: number
  newIndex: number
}

function assertSortableIndex(index: number, length: number, name: string) {
  if (!Number.isInteger(index) || index < 0 || index >= length) {
    throw new RangeError(`${name} ${index} is outside the sortable list bounds`)
  }
}

function normalizeTargetIndex(oldIndex: number, newIndex: number, length: number) {
  assertSortableIndex(oldIndex, length, 'oldIndex')
  assertSortableIndex(newIndex, length, 'newIndex')
  return newIndex
}

export function reorder<T>(items: readonly T[], oldIndex: number, newIndex: number): ReorderResult<T> {
  const nextItems = Array.from(items)
  const targetIndex = normalizeTargetIndex(oldIndex, newIndex, nextItems.length)
  const [item] = nextItems.splice(oldIndex, 1)
  nextItems.splice(targetIndex, 0, item)

  return {
    items: nextItems,
    item,
    oldIndex,
    newIndex: targetIndex,
  }
}

export function moveItem<T>(items: T[], oldIndex: number, newIndex: number): ReorderResult<T> {
  const result = reorder(items, oldIndex, newIndex)
  items.splice(0, items.length, ...result.items)
  return {
    ...result,
    items,
  }
}

export function create(element: SortableElement, options: SortableOptions = {}): SortableInstance {
  return SortableJs.create(element, options as any) as SortableInstance
}

export function manage(instance: SortableInstance): ManagedSortableInstance {
  const managed = instance as ManagedSortableInstance
  let released = false

  managed.release = () => {
    if (released) {
      return
    }

    released = true
    managed.destroy()
  }

  return managed
}

export function register(owner: SortableOwner, instance: SortableInstance): ManagedSortableInstance {
  const managed = manage(instance)
  const context = owner && ('context' in owner ? owner.context : owner)

  if (!context || typeof context.addUnnamedResource !== 'function') {
    return managed
  }

  context.addUnnamedResource('GENERIC_RESOURCE', managed)
  return managed
}

export function createManaged(owner: SortableOwner, element: SortableElement, options: SortableOptions = {}): ManagedSortableInstance {
  return register(owner, create(element, options))
}

export function mount(...plugins: unknown[]) {
  return SortableJs.mount(...plugins as any[])
}

const Sortable = {
  Raw: SortableJs,
  create,
  createManaged,
  manage,
  register,
  mount,
  reorder,
  moveItem,
}

export default Sortable
